import { createFileRoute } from '@tanstack/react-router'
import { format, isValid } from 'date-fns'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  IconCertificate,
  IconDownload,
  IconListDetails,
} from '@tabler/icons-react'
import { useCpdSummaryQuery } from '@/features/dashboard/api/useCpdSummaryQuery'
import type { CpdSummary } from '@/features/dashboard/api/useCpdSummaryQuery'
import Certificate from '@/features/membership/components/CertificateView'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

function formatDateLabel(value: string | null, pattern: string, fallback: string): string {
  if (!value) return fallback
  const date = new Date(value)
  if (!isValid(date)) return fallback
  return format(date, pattern)
}

function CpdBankPage() {
  const cpdSummaryQuery = useCpdSummaryQuery()
  type SummaryEvent = CpdSummary['recentEvents'][number]

  // console.log("cpdSummaryQuery: ", cpdSummaryQuery);
  

  // Use query data if available, otherwise fall back to demoSummary for design/demo purposes
  const cpdData = !cpdSummaryQuery.isPending && cpdSummaryQuery.data ? cpdSummaryQuery.data : null

  // Certificate preview modal state (holds the selected event to preview)
  const [previewEvent, setPreviewEvent] = useState<SummaryEvent | null>(null)
  const [pendingDownloadId, setPendingDownloadId] = useState<string | null>(null)
  const [closeAfterDownload, setCloseAfterDownload] = useState(false)
  const certRef = useRef<HTMLDivElement | null>(null)

  const waitForCertificateNode = useCallback(async (): Promise<HTMLDivElement> => {
    const maxFrames = 30
    for (let frame = 0; frame < maxFrames; frame += 1) {
      if (certRef.current) return certRef.current
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve())
      })
    }
    throw new Error('Certificate preview is not ready yet.')
  }, [])

  const waitForCertificateImages = useCallback(async (node: HTMLElement): Promise<void> => {
    const images = Array.from(node.querySelectorAll('img'))
    if (!images.length) return

    await Promise.all(
      images.map((image) => {
        if (image.complete && image.naturalWidth > 0) return Promise.resolve()

        return new Promise<void>((resolve) => {
          const cleanup = () => {
            image.removeEventListener('load', onLoad)
            image.removeEventListener('error', onError)
          }

          const onLoad = () => {
            cleanup()
            resolve()
          }
          const onError = () => {
            cleanup()
            resolve()
          }

          image.addEventListener('load', onLoad, { once: true })
          image.addEventListener('error', onError, { once: true })

          setTimeout(() => {
            cleanup()
            resolve()
          }, 2000)
        })
      }),
    )
  }, [])

  const exportCurrentCertificate = useCallback(async (eventToExport: SummaryEvent | null) => {
    if (!eventToExport) return

    try {
      const certificateNode = await waitForCertificateNode()
      await waitForCertificateImages(certificateNode)

      // html-to-image uses the browser's native rendering (SVG foreignObject),
      // so it fully supports oklch colors, images, and all modern CSS.
      const { toPng } = await import('html-to-image')
      const imgData = await toPng(certificateNode, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
      })

      const jsPDF = (await import('jspdf')).default
      // Measure the image to set PDF page size
      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = reject
        img.src = imgData
      })
      const doc = new jsPDF({ unit: 'px', format: [img.width, img.height] })
      doc.addImage(imgData, 'PNG', 0, 0, img.width, img.height)
      const filename = `${(eventToExport?.title || 'certificate').replace(/[^a-z0-9-_\.]/gi, '_')}.pdf`
      doc.save(filename)
    } catch (err) {
      console.error('Failed to export certificate', err)
      toast.error('Unable to download the certificate. Please try again.')
    }
  }, [waitForCertificateImages, waitForCertificateNode])

  useEffect(() => {
    if (!pendingDownloadId) return
    if (!previewEvent || previewEvent.id !== pendingDownloadId) return

    void (async () => {
      await exportCurrentCertificate(previewEvent)
      if (closeAfterDownload) {
        setPreviewEvent(null)
        setCloseAfterDownload(false)
      }
      setPendingDownloadId(null)
    })()
  }, [closeAfterDownload, exportCurrentCertificate, pendingDownloadId, previewEvent])

  const handlePreview = useCallback((event: SummaryEvent) => {
    if (!event?.certificateAvailable) return
    setCloseAfterDownload(false)
    setPendingDownloadId(null)
    setPreviewEvent(event)
  }, [])

  const handleDownload = useCallback((event: SummaryEvent) => {
    if (!event?.certificateAvailable) return
    if (previewEvent?.id === event.id && certRef.current) {
      void exportCurrentCertificate(event)
      return
    }

    setCloseAfterDownload(true)
    setPendingDownloadId(event.id)
    setPreviewEvent(event)
  }, [exportCurrentCertificate, previewEvent])

  if (cpdSummaryQuery.isError) {
    return (
      <div
        role="alert"
        className="border-destructive/40 bg-destructive/10 text-destructive flex flex-col gap-2 rounded-md border p-4"
      >
        <p className="font-semibold">Unable to load CPD history</p>
        <p className="text-sm">
          {cpdSummaryQuery.error?.message ?? 'Please try refreshing the page or check again in a few minutes.'}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <IconCertificate className="text-primary size-8" aria-hidden />
          <div>
            <h1 className="text-2xl font-semibold">CPD Bank</h1>
            <p className="text-muted-foreground text-sm">
              Review your CPD activity and download certificates for completed sessions.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" disabled>
          <IconDownload className="mr-2 size-4" aria-hidden />
          Export CSV (coming soon)
        </Button>
      </div>

      <Card className="border-muted">
        <CardHeader className="gap-2">
          <CardTitle className="flex items-center gap-2 text-xl">
            <IconListDetails className="text-primary size-5" aria-hidden />
            Activity summary
          </CardTitle>
          <CardDescription>
            Track your progress towards the annual CPD target. Filtering and certificate downloads will plug into the API in the next slice.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {cpdSummaryQuery.isPending ? (
            <div className="grid gap-3 sm:grid-cols-3">
              {[0, 1, 2].map(item => (
                <div
                  key={`cpd-skeleton-${item}`}
                  className="bg-muted/30 flex flex-col gap-2 rounded-md p-3"
                >
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="bg-muted/30 rounded-md p-3">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Year</p>
                <p className="text-lg font-semibold">{cpdData?.year ?? '—'}</p>
              </div>
              <div className="bg-muted/30 rounded-md p-3">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Points earned</p>
                <p className="text-lg font-semibold">{cpdData?.totalPoints ?? 0}</p>
              </div>
              <div className="bg-muted/30 rounded-md p-3">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Events completed</p>
                <p className="text-lg font-semibold">{cpdData?.eventsCompleted ?? 0}</p>
              </div>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Points (earned / event)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Certificate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cpdSummaryQuery.isPending && (
                <>
                  {[0, 1, 2].map(item => (
                    <TableRow key={`row-skeleton-${item}`}>
                      <TableCell><Skeleton className="h-4 w-[240px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[160px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[64px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="ml-auto h-8 w-[110px]" /></TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              {!cpdSummaryQuery.isPending && cpdData?.recentEvents.map(event => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">{event.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateLabel(event.completedOn, 'EEEE d MMMM yyyy', 'Date not available')}
                  </TableCell>
                  <TableCell>
                    {event.pointsEarned ?? 0}
                    {typeof event.eventPoints === 'number' && Number.isFinite(event.eventPoints) ? ` / ${event.eventPoints}` : ''}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {event.certificateAvailable ? 'Completed' : 'Pending certificate'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={!event.certificateAvailable}
                        onClick={() => handleDownload(event)}
                      >
                        <IconDownload className="mr-2 size-4" aria-hidden />
                        {event.certificateAvailable ? 'Download' : 'Processing'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePreview(event)}
                        disabled={!event.certificateAvailable}
                      >
                        Preview
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog
        open={!!previewEvent}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewEvent(null)
            setPendingDownloadId(null)
            setCloseAfterDownload(false)
          }
        }}
      >
        <DialogContent className="flex w-full max-w-[min(95vw,1200px)] max-h-[90vh] flex-col overflow-hidden p-0 sm:w-auto sm:max-w-[min(90vw,1200px)]">
          <DialogHeader className="flex items-center justify-between border-b px-6 py-4">
            <DialogTitle>Certificate preview</DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await exportCurrentCertificate(previewEvent)
                }}
              >
                <IconDownload className="mr-2 size-4" />
                Download
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPreviewEvent(null)}>Close</Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-auto bg-muted/10 px-6 py-6">
            {previewEvent ? (
              <div ref={certRef} className="mx-auto w-full max-w-4xl">
                <Certificate
                  recipientName={previewEvent.recipientName || previewEvent.memberName || 'Participant Name'}
                  eventName={previewEvent.title}
                  theme={previewEvent.theme}
                  trainingDate={formatDateLabel(previewEvent.completedOn, 'EEEE d MMMM yyyy', 'Date not available')}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground p-6">No certificate available to preview.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/benefits/cpd-bank')({
  component: CpdBankPage,
})
